import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, interval, Subscription, of, from } from 'rxjs';
import { switchMap, takeUntil, catchError, map } from 'rxjs/operators';
import { AuthHTTPService } from './auth-http/auth-http.service';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TwoFactorComponent } from '../components/two-factor/two-factor.component';
import { AuthService } from './auth.service';
import { PortalDialogService } from '../../../services/portal-dialog.service';

export interface MfaMonitorResponse {
  mfa_verify_required: boolean;
  mfa_status?: {
    enabled: boolean;
    type: string;
    methods: any[];
  };
  mfa_last?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MfaMonitorService implements OnDestroy {
  private destroy$ = new BehaviorSubject<void>(undefined);
  private mfaCheckInterval: Subscription | null = null;
  private isChecking = false;
  
  // BehaviorSubject to track if MFA verification is currently required
  private mfaVerificationRequiredSubject = new BehaviorSubject<boolean>(false);
  public mfaVerificationRequired$ = this.mfaVerificationRequiredSubject.asObservable();

  // BehaviorSubject to track if MFA dialog is currently open
  private mfaDialogOpenSubject = new BehaviorSubject<boolean>(false);
  public mfaDialogOpen$ = this.mfaDialogOpenSubject.asObservable();

  // MFA traffic light status
  private mfaStatusSubject = new BehaviorSubject<'green' | 'orange' | 'red'>('green');
  public mfaStatus$ = this.mfaStatusSubject.asObservable();

  // Step-up recency window (ms) for sensitive operations
  private static readonly STEP_UP_WINDOW_MS = 10 * 60 * 1000; // 10 minutes

  /** Matches API HIGH_RISK_MFA_MAX_AGE_SECONDS default (300). */
  public static readonly HIGH_RISK_WINDOW_MS = 5 * 60 * 1000;

  private openDialogRef: NgbModalRef | null = null;

  constructor(
    private authHttpService: AuthHTTPService,
    private dialog: PortalDialogService,
    private authService: AuthService
  ) {}

  /**
   * Start periodic MFA verification checks
   * @param intervalMs - Interval in milliseconds (default: 30 seconds)
   */
  startMfaMonitoring(intervalMs: number = 30000): void {
    if (this.mfaCheckInterval) {
      console.log('[MfaMonitor] MFA monitoring already started');
      return;
    }

    console.log('[MfaMonitor] Starting MFA monitoring with interval:', intervalMs, 'ms');
    
    // Perform an immediate check so we don't wait for the first interval tick
    this.checkMfaStatus().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        // Do not auto-open dialog; status is handled below
      },
      error: (error) => {
        console.error('[MfaMonitor] Error during immediate MFA check:', error);
      }
    });

    this.mfaCheckInterval = interval(intervalMs).pipe(
      takeUntil(this.destroy$),
      switchMap(() => this.checkMfaStatus())
    ).subscribe({
      next: (response) => {
        // Do not auto-open dialog; status is handled below
      },
      error: (error) => {
        console.error('[MfaMonitor] Error during MFA check:', error);
      }
    });
  }

  /**
   * Stop periodic MFA verification checks
   */
  stopMfaMonitoring(): void {
    if (this.mfaCheckInterval) {
      console.log('[MfaMonitor] Stopping MFA monitoring');
      this.mfaCheckInterval.unsubscribe();
      this.mfaCheckInterval = null;
    }
  }

  /**
   * Check MFA status by fetching current contact context
   * Note: /auth/contact does not include MFA details; this returns defaults unless provided elsewhere
   */
  private checkMfaStatus(): Observable<MfaMonitorResponse | null> {
    if (this.isChecking) {
      console.log('[MfaMonitor] MFA check already in progress, skipping');
      return new Observable<MfaMonitorResponse | null>(subscriber => subscriber.next(null));
    }

    this.isChecking = true;
    const token = localStorage.getItem('authToken');

    if (!token) {
      this.isChecking = false;
      return new Observable<MfaMonitorResponse | null>(subscriber => subscriber.next(null));
    }

    // Skip while the user is completing MFA enroll/verify after email login.
    if (localStorage.getItem('mfaPending') === '1') {
      this.isChecking = false;
      return of(null);
    }

    return this.authHttpService.getUserByToken(token).pipe(
      map((user: any) => {
        this.isChecking = false;
        
        if (!user) {
          // Failed to get user even after potential interceptor refresh
          this.setStatus('red');
          return null;
        }

        // Extract MFA information from user response
        const mfaInfo: MfaMonitorResponse = {
          mfa_verify_required: user.mfa_verify_required || false,
          mfa_status: user.mfa_status,
          mfa_last: user.mfa_last
        };

        // Suppress MFA prompt shortly after a successful passkey sign-in
        try {
          const lastMethod = localStorage.getItem('lastAuthMethod');
          const lastAtStr = localStorage.getItem('lastAuthAt');
          const lastAt = lastAtStr ? parseInt(lastAtStr, 10) : 0;
          const now = Date.now();
          const within5Min = lastAt > 0 && (now - lastAt) < 5 * 60 * 1000;
          if (lastMethod === 'passkey' && within5Min) {
            mfaInfo.mfa_verify_required = false;
          }
        } catch {}

        console.log('[MfaMonitor] MFA status check result:', mfaInfo);

        // Compute traffic-light status
        const now = Date.now();
        const lastMfaStr = localStorage.getItem('lastMfaVerifiedAt');
        const lastMfa = lastMfaStr ? parseInt(lastMfaStr, 10) : 0;
        const lastAuthStr = localStorage.getItem('lastAuthAt');
        const lastAuth = lastAuthStr ? parseInt(lastAuthStr, 10) : 0;
        const lastStrong = Math.max(lastMfa, lastAuth);

        if (mfaInfo.mfa_verify_required) {
          this.setStatus('red');
        } else if (lastStrong === 0 || (now - lastStrong) > MfaMonitorService.STEP_UP_WINDOW_MS) {
          this.setStatus('orange');
        } else {
          this.setStatus('green');
        }
        return mfaInfo;
      }),
      catchError((error) => {
        this.isChecking = false;
        console.error('[MfaMonitor] Error checking MFA status (likely token expired):', error);
        // Do not auto-start WebAuthn here: it sets global auth loading and strands
        // the email sign-in button ("Even geduld...") if the user cancels or leaves.
        // A failed profile check must not clear portal_session; that sent users
        // back to sign-in a few seconds after a successful login.
        this.setStatus('red');
        this.authService.clearLoading();
        return of(null);
      })
    );
  }

  /**
   * Show MFA verification dialog as a modal (idempotent).
   * Opens immediately; Observable resolves true on success, false on dismiss.
   */
  public showMfaDialog(): Observable<boolean> {
    if (this.openDialogRef) {
      return from(
        this.openDialogRef.result.then(
          (ok) => ok === true,
          () => false
        )
      );
    }

    this.mfaDialogOpenSubject.next(true);
    const ref: NgbModalRef = this.dialog.open(TwoFactorComponent);
    this.openDialogRef = ref;

    const settled = ref.result.then(
      (ok) => {
        this.openDialogRef = null;
        this.mfaDialogOpenSubject.next(false);
        return ok === true;
      },
      () => {
        this.openDialogRef = null;
        this.mfaDialogOpenSubject.next(false);
        return false;
      }
    );

    return from(settled);
  }

  /** Ensure a recent MFA verification exists; opens dialog if needed. */
  public ensureRecentMfa(windowMs: number = MfaMonitorService.STEP_UP_WINDOW_MS): Observable<boolean> {
    if (!this.needsStepUp(windowMs)) {
      return of(true);
    }
    return this.showMfaDialog();
  }

  /** High-risk portal ops (M365 management plans): MFA ≤ 5 minutes. */
  public ensureHighRiskMfa(force = false): Observable<boolean> {
    if (!force && !this.needsStepUp(MfaMonitorService.HIGH_RISK_WINDOW_MS)) {
      return of(true);
    }
    return this.showMfaDialog();
  }

  private needsStepUp(windowMs: number): boolean {
    const now = Date.now();
    const lastMfaStr = localStorage.getItem('lastMfaVerifiedAt');
    const lastMfa = lastMfaStr ? parseInt(lastMfaStr, 10) : 0;
    const lastAuthStr = localStorage.getItem('lastAuthAt');
    const lastAuth = lastAuthStr ? parseInt(lastAuthStr, 10) : 0;
    const lastStrong = Math.max(lastMfa, lastAuth);
    return !(lastStrong > 0 && now - lastStrong <= windowMs);
  }

  /** Whether the MFA modal is currently open */
  public isDialogOpen(): boolean {
    return this.mfaDialogOpenSubject.value;
  }

  /**
   * Close MFA verification dialog
   */
  closeMfaDialog(): void {
    this.mfaVerificationRequiredSubject.next(false);
    this.mfaDialogOpenSubject.next(false);
    if (this.openDialogRef) {
      const ref = this.openDialogRef;
      this.openDialogRef = null;
      ref.dismiss('closed');
    }
  }

  /**
   * Handle successful MFA verification
   */
  onMfaVerificationSuccess(): void {
    console.log('[MfaMonitor] MFA verification successful');
    this.closeMfaDialog();
    
    // Update the MFA verification required status
    this.mfaVerificationRequiredSubject.next(false);
    try {
      localStorage.setItem('lastMfaVerifiedAt', String(Date.now()));
    } catch {}
    this.setStatus('green');
  }

  /**
   * Check if MFA monitoring is currently active
   */
  isMonitoringActive(): boolean {
    return this.mfaCheckInterval !== null;
  }

  ngOnDestroy(): void {
    this.stopMfaMonitoring();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setStatus(status: 'green' | 'orange' | 'red'): void {
    this.mfaStatusSubject.next(status);
  }
}
